import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Server, AlertCircle, Wrench } from 'lucide-react';
import { config } from '../../config';

interface DashboardCounts {
  components: number;
  activeIncidents: number;
  maintenances: number;
}

export default function AdminDashboard() {
  const [counts, setCounts] = useState<DashboardCounts>({ components: 0, activeIncidents: 0, maintenances: 0 });
  const [loading, setLoading] = useState(true);

  const apiUrl = config.apiUrl;

  useEffect(() => {
    fetchCounts();
  }, []);

  async function fetchCounts() {
    try {
      const [componentsRes, incidentsRes, maintenancesRes] = await Promise.all([
        fetch(`${apiUrl}/v1/stats/components`),
        fetch(`${apiUrl}/v1/stats/incidents`),
        fetch(`${apiUrl}/v1/stats/maintenances`),
      ]);

      const components = componentsRes.ok ? await componentsRes.json() : [];
      const incidents = incidentsRes.ok ? await incidentsRes.json() : [];
      const maintenances = maintenancesRes.ok ? await maintenancesRes.json() : [];

      setCounts({
        components: Array.isArray(components) ? components.length : 0,
        activeIncidents: Array.isArray(incidents) ? incidents.length : 0,
        maintenances: Array.isArray(maintenances) ? maintenances.length : 0,
      });
    } catch (error) {
      console.error('Failed to fetch dashboard counts', error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="animate-fade-in">
      <h2 className="text-lg font-bold text-navy-900 mb-5">Dashboard</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-card border border-gray-100 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Components</p>
              <p className="text-2xl font-bold text-navy-900 mt-1.5 tabular-nums">
                {loading ? '—' : counts.components}
              </p>
            </div>
            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
              <Server className="h-5 w-5 text-blue-500" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-card border border-gray-100 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Active Incidents</p>
              <p className="text-2xl font-bold text-navy-900 mt-1.5 tabular-nums">
                {loading ? '—' : counts.activeIncidents}
              </p>
            </div>
            <div className="w-10 h-10 bg-rose-50 rounded-xl flex items-center justify-center">
              <AlertCircle className="h-5 w-5 text-rose-300" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-card border border-gray-100 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Maintenances</p>
              <p className="text-2xl font-bold text-navy-900 mt-1.5 tabular-nums">
                {loading ? '—' : counts.maintenances}
              </p>
            </div>
            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
              <Wrench className="h-5 w-5 text-blue-500" />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <h3 className="text-sm font-bold text-navy-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/login/components"
            className="bg-white p-5 border border-gray-100 rounded-xl hover:shadow-card-hover hover:border-gray-200 transition-all duration-200 group"
          >
            <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center mb-3 group-hover:bg-blue-100 transition-colors">
              <Server className="h-4.5 w-4.5 text-blue-600" />
            </div>
            <h4 className="text-sm font-semibold text-navy-900">Manage Components</h4>
            <p className="text-xs text-gray-400 mt-1">Add, edit, or update component status</p>
          </Link>
          <Link
            to="/login/incidents"
            className="bg-white p-5 border border-gray-100 rounded-xl hover:shadow-card-hover hover:border-gray-200 transition-all duration-200 group"
          >
            <div className="w-9 h-9 bg-rose-50 rounded-lg flex items-center justify-center mb-3 group-hover:bg-rose-100 transition-colors">
              <AlertCircle className="h-4.5 w-4.5 text-rose-300" />
            </div>
            <h4 className="text-sm font-semibold text-navy-900">Create Incident</h4>
            <p className="text-xs text-gray-400 mt-1">Report and manage service incidents</p>
          </Link>
          <Link
            to="/login/maintenances"
            className="bg-white p-5 border border-gray-100 rounded-xl hover:shadow-card-hover hover:border-gray-200 transition-all duration-200 group"
          >
            <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center mb-3 group-hover:bg-blue-100 transition-colors">
              <Wrench className="h-4.5 w-4.5 text-blue-600" />
            </div>
            <h4 className="text-sm font-semibold text-navy-900">Schedule Maintenance</h4>
            <p className="text-xs text-gray-400 mt-1">Plan upcoming maintenance windows</p>
          </Link>
        </div>
      </div>
    </div>
  );
}
